import * as SliderPrimitive from '@radix-ui/react-slider'
import React, { useState } from 'react'

import { cn } from '@/lib/utils'

interface RangeSliderProps {
  className?: string
  min: number
  max: number
  step: number
  formatLabel?: (value: number) => string
  value?: number[] | readonly number[]
  onValueChange?: (values: number[]) => void
}

const RangeSlider = React.forwardRef(
  (
    { className, formatLabel, max, min, onValueChange, step, value, ...props }: RangeSliderProps,
    ref
  ) => {
    const initialValue = Array.isArray(value) ? value : [min, max]
    const [localValues, setLocalValues] = useState<number[]>(initialValue)

    const handleValueChange = (newValues: number[]) => {
      setLocalValues(newValues)
      if (onValueChange) {
        onValueChange(newValues)
      }
    }

    const disabledStyles = min === max ? 'cursor-not-allowed' : ''

    return (
      <div className="flex w-full gap-2 items-center">
        <div className="w-min text-[14px] text-primary">{formatLabel ? formatLabel(min) : min}</div>
        <SliderPrimitive.Root
          className={cn(
            'w-98 relative flex h-5 w-full touch-none select-none items-center',
            className
          )}
          disabled={min === max}
          max={max === min ? max + 1 : max}
          min={min}
          onValueChange={handleValueChange}
          ref={ref as React.RefObject<HTMLDivElement>}
          step={step}
          value={max === min ? [min, max + 1] : localValues}
          {...props}
        >
          <SliderPrimitive.Track
            className={cn(
              'relative h-1 w-full grow overflow-hidden rounded-full bg-gray-300',
              disabledStyles
            )}
          >
            <SliderPrimitive.Range className="absolute h-full bg-black" />
          </SliderPrimitive.Track>
          {localValues.map((value, index) => (
            <React.Fragment key={index}>
              <SliderPrimitive.Thumb
                className={cn(
                  `block h-3 w-3 rounded-full border-2 border-black
                 bg-white ring-offset-white transition-colors focus-visible:outline-none 
                   focus-visible:ring-1 focus-visible:ring-slate-950 focus-visible:ring-offset-2`,
                  disabledStyles
                )}
              >
                {value === min || value === max ? null : (
                  <div
                    className="absolute text-xs top-4 left-1/2 transform -translate-x-1/2 
                    z-30 bg-primary border rounded-md text-white shadow-sm px-2 bg-black"
                  >
                    {formatLabel ? formatLabel(value) : value}
                  </div>
                )}
              </SliderPrimitive.Thumb>
            </React.Fragment>
          ))}
        </SliderPrimitive.Root>
        <div className="w-min text-[14px]">{formatLabel ? formatLabel(max) : max}</div>
      </div>
    )
  }
)

RangeSlider.displayName = SliderPrimitive.Root.displayName

export { RangeSlider }
